import { lazy, Suspense, useCallback, useState } from 'react';
import classNames from 'classnames';
import TrailDetailsPhotoSource from '@alltrails/analytics/enums/TrailDetailsPhotoSource';
import ReportLocation from '@alltrails/analytics/enums/ReportLocation';
import { PhotoSize } from '@alltrails/shared/types/photos';
import type { Context } from 'types/Context';
import { LightboxPhoto } from '@alltrails/shared/types/lightbox';
import { useTrailUgcContext } from 'components/trailUgc/TrailUgcContext';
import useTrailAggregatePhotoViewed from 'hooks/useTrailAggregatePhotoViewed';
import { Photo } from 'types/Photo';
import { urlReformatPhotos } from 'utils/lightbox_helpers';
import * as styles from './styles.module.scss';
import UgcEmpty from '../UgcEmpty';
import useUgcContentState, { UgcContentProps } from '../hooks/useUgcContentState';
import PhotosUgcItem from '../PhotosUgcItem';
import SortOption from '../types/SortOption';

import '@alltrails/modules/PhotoUpload/sharedCss.css';

const Lightbox = lazy(() => import('components/shared/Lightbox'));
const PhotoUploadModal = lazy(() => import('@alltrails/modules/PhotoUpload/PhotoUploadModal'));

export type PhotosUgcProps = UgcContentProps & {
  baseUrl: string;
  dynamicPhotoSizes?: boolean;
  hideSortLabel?: boolean;
  photoSize: PhotoSize;
  showContributeButton?: boolean;
  showInvitation?: boolean;
  trailName?: string;
  context: Context;
};

const PhotosUgc = ({
  baseUrl,
  dynamicPhotoSizes,
  locationServerData,
  hideSortLabel,
  perPage,
  photoSize,
  showContributeButton = true,
  showInvitation = true,
  trailId,
  userId,
  trailName,
  context
}: PhotosUgcProps): JSX.Element => {
  const {
    ugcState: {
      photos: { items, total, pageCount }
    },
    dispatch
  } = useTrailUgcContext();
  const [lightboxState, setLightboxState] = useState({ isOpen: false, startingPhotosIndex: null });
  const [updateAggregate, logAggregateViewed] = useTrailAggregatePhotoViewed(
    trailId ? { source: TrailDetailsPhotoSource.PhotosGallery, trailId } : undefined
  );

  const openLightbox = (photoIndex: number) => {
    setLightboxState({ isOpen: true, startingPhotosIndex: photoIndex });
  };

  const closeLightbox = () => {
    logAggregateViewed();
    setLightboxState({ isOpen: false, startingPhotosIndex: null });
  };

  const onPhotoViewed = (photo: LightboxPhoto['photo']) => {
    updateAggregate(photo.id);
  };

  const handlePhotosAdd = (data: { detail: Photo[] }) => {
    dispatch({ type: 'ADD_PHOTOS', photos: data.detail });
  };

  const handleMorePhotosLoaded = useCallback(
    (data: any) => {
      const newData = (data.photos as Photo[]).map(photo => ({
        id: photo.id,
        photoHash: photo.photoHash,
        locationName: photo.locationName
      }));

      dispatch({ type: 'LOAD_MORE_PHOTOS', photos: newData });
    },
    [dispatch]
  );

  const { formOpen, closeForm, renderUgcBar, renderUgcShowMore } = useUgcContentState<Photo>({
    items,
    activeTab: 'photos',
    pageCount,
    maxItems: total,
    locationServerData,
    userId,
    perPage,
    handleMoreLoaded: handleMorePhotosLoaded,
    trailId,
    sortConfig: {
      availableSorts: ['atSort', 'desc', 'asc'],
      hideSortLabel,
      onSortSuccess: (data: any, newSort: SortOption) => {
        const newPhotos = data.photos.map((photo: Photo) => ({
          id: photo.id,
          photoHash: photo.photoHash
        }));
        dispatch({ type: 'SORT_PHOTOS', payload: { photos: newPhotos, newSort } });
      },
      sortUrl: `${baseUrl}/photos/`
    } /* sortConfig */,
    showContributeButton,
    showInvitation
  });

  return (
    <>
      {renderUgcBar()}
      {items?.length ? (
        <div className={classNames({ [styles.photosGallery]: dynamicPhotoSizes, [styles.photosGrid]: !dynamicPhotoSizes })}>
          {items.map((photo, i) => (
            <PhotosUgcItem
              key={photo.id}
              idx={i}
              item={photo}
              photoSize={photoSize}
              openLightbox={openLightbox}
              dynamicPhotoSizes={dynamicPhotoSizes}
              trailName={trailName}
            />
          ))}
        </div>
      ) : (
        <UgcEmpty tab="photos" />
      )}
      {formOpen && (
        <Suspense fallback={null}>
          <PhotoUploadModal closePhotoModal={closeForm} onPhotosAdd={handlePhotosAdd} trailId={trailId} />
        </Suspense>
      )}
      {lightboxState.isOpen && (
        <Suspense fallback={null}>
          <Lightbox
            images={urlReformatPhotos(items)}
            handleCloseRequest={closeLightbox}
            onPhotoViewed={onPhotoViewed}
            startingIndex={lightboxState.startingPhotosIndex}
            trailId={trailId}
            context={context}
            analyticsReportLocation={ReportLocation.TrailDetails}
          />
        </Suspense>
      )}
      <div className={styles.showMoreContainer}>{renderUgcShowMore()}</div>
    </>
  );
};

export default PhotosUgc;
